/**
 * @file formulationDates.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 * @description The date of a formulation entry (an iteration) — one rule for
 *              the screens and the server (utils/formulationDates.js is the
 *              generated copy; scripts/sync-shared-utils.js).
 *
 *   Cam, 14 Oct 2026: the date is the user's, chosen when a formulation is
 *   added (empty until they choose, and required) and editable afterwards.
 *
 *   - A date is a London calendar day 'YYYY-MM-DD', never in the future.
 *   - The entry is stored at NOON London on that day, so its UTC date is the
 *     same day all year (the database session is UTC — midnight London in
 *     summer is the previous UTC day) and its pricing period follows the day.
 *   - Newest first = the later DAY, then the later-saved entry (higher id) on
 *     the same day. A back-dated entry never overtakes a newer one.
 *   - Editing: the figures or notes changed → a NEW entry on the date in the
 *     field; only the date changed → that entry moves to the new date;
 *     neither → nothing to save.
 */

const LONDON = 'Europe/London';
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

// Nothing before this is a real formulation date — the oldest entry on record
// is December 2019. Stops a mistyped year (0204) being saved as history.
export const EARLIEST_FORMULATION_DAY = '2000-01-01';

/** Is this a real calendar day written 'YYYY-MM-DD'? */
export function isIsoDay(value) {
  const m = ISO_DAY.exec(String(value || ''));
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1])
    && d.getUTCMonth() === Number(m[2]) - 1
    && d.getUTCDate() === Number(m[3]);
}

/**
 * A value as its London calendar day 'YYYY-MM-DD', or null. A plain day is
 * taken as written; a timestamp (or Date) is read in London time.
 */
export function londonDayOf(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && isIsoDay(value)) return value;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON, year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Today in London. `now` is injectable for tests. */
export function todayLondonDay(now = new Date()) {
  return londonDayOf(now);
}

/** 'YYYY-MM-DD' as DD/MM/YYYY, the way every date in the system reads. */
export function formatFormulationDay(day) {
  const m = ISO_DAY.exec(String(day || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

/**
 * Why this date cannot be a formulation's, or null when it can.
 * Required, a real day, not in the future, not before 2000.
 */
export function formulationDateProblem(day, today = todayLondonDay()) {
  if (day === null || day === undefined || String(day).trim() === '') {
    return 'Choose the formulation date.';
  }
  if (!isIsoDay(day)) return 'The formulation date is not a date — use DD/MM/YYYY.';
  if (day > today) {
    return `The formulation date cannot be in the future (today is ${formatFormulationDay(today)}).`;
  }
  if (day < EARLIEST_FORMULATION_DAY) {
    return `The formulation date cannot be before ${formatFormulationDay(EARLIEST_FORMULATION_DAY)}.`;
  }
  return null;
}

/** The day an entry is dated (its stored stamp read in London). */
export function iterationDay(iteration) {
  return londonDayOf(iteration?.created_at);
}

/**
 * Newest first: the later day, then the later-saved entry on the same day.
 * The SQL readers order the same way (formulationService.ITERATION_ORDER).
 */
export function compareIterationsNewestFirst(a, b) {
  const da = iterationDay(a) || '';
  const db = iterationDay(b) || '';
  if (da !== db) return da < db ? 1 : -1;
  return Number(b?.id || 0) - Number(a?.id || 0);
}

/**
 * What saving an edit does.
 *   figuresChanged  the component values or the notes differ from the entry
 *   baseDay         the day of the entry being edited (null: none)
 *   day             the day in the Date field
 * → 'new' (a new entry on `day`), 'redate' (move the entry to `day`) or
 *   'unchanged'.
 */
export function editOutcome({ figuresChanged, baseDay, day }) {
  if (figuresChanged || !baseDay) return 'new';
  if (day && day !== baseDay) return 'redate';
  return 'unchanged';
}
